<template>
  <div class="dcm-page">
    <MenuBar />
    <div class="content">
      <div class="page-header">
        <h1>DCM Management Operations</h1>
        <p>Campaign operations are managed in a status-driven workflow with assignment and progress tracking.</p>
      </div>

      <div v-if="isDcmOrSuper" class="panel">
        <h2>Operation Task Table</h2>
        <p class="sub">Filter and proceed each campaign through: Campaign Initiated → Task Accepted → Creative Production → Creative Approved → Publishing → Completed.</p>
        <p v-if="message" class="message-banner">{{ message }}</p>

        <div class="filters">
          <label>
            <span>From Date</span>
            <input type="date" v-model="filters.fromDate" />
          </label>
          <label>
            <span>To Date</span>
            <input type="date" v-model="filters.toDate" />
          </label>
          <label>
            <span>Status</span>
            <select v-model="filters.status">
              <option value="">All</option>
              <option value="campaign_initiated">Campaign Initiated</option>
              <option value="task_accepted">Task Accepted</option>
              <option value="creative_production">Creative Production</option>
              <option value="creative_approved">Creative Approved</option>
              <option value="publishing">Publishing</option>
              <option value="completed">Completed</option>
            </select>
          </label>
          <label>
            <span>Assigned Person</span>
            <select v-model="filters.assignedPerson">
              <option value="">All</option>
              <option v-for="creative in creativeUsers" :key="creative.username" :value="creative.username">{{ creative.username }}</option>
            </select>
          </label>
        </div>

        <div v-if="!filteredOperations.length" class="empty-state">No campaign operations found for selected filters.</div>

        <div v-else class="table-wrap ops-table-wrap">
          <table class="ops-table">
            <thead>
              <tr>
                <th>Campaign Name</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Total Content</th>
                <th>Status</th>
                <th>Assigned Person</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredOperations" :key="row.notificationId" :class="statusRowClass(row.status)">
                <td>
                  <strong>{{ row.campaignName || '-' }}</strong>
                  <p class="mini">{{ row.sponsorName || '-' }}</p>
                </td>
                <td>{{ humanDate(row.startDate) }}</td>
                <td>{{ humanDate(row.endDate) }}</td>
                <td>{{ row.numberOfContent || '-' }}</td>
                <td><span class="status-pill" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span></td>
                <td>{{ row.assignedCreative || '-' }}</td>
                <td class="action-cell">
                  <button class="btn ghost small" @click="openDetails(row)">Details</button>
                  <button
                    v-if="canAcceptCampaignStatus(row.status)"
                    class="btn small"
                    @click="acceptOperation(row)"
                  >Accept Task</button>
                  <button
                    v-else-if="canAssignCreativeStatus(row.status)"
                    class="btn small"
                    @click="openAssignPanel(row)"
                  >Assign Creative</button>
                  <button
                    v-else-if="canPublishCampaignStatus(row.status)"
                    class="btn small secondary"
                    @click="openPublishing(row)"
                  >Open Publishing</button>
                  <span v-else class="mini">{{ isCompletedCampaignStatus(row.status) ? 'Completed' : 'Waiting' }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="selectedOperation" class="assign-panel">
          <h3>Proceed: Assign Creative</h3>
          <p>
            Campaign: <strong>{{ selectedOperation.campaignName }}</strong>
          </p>

          <div class="assign-grid">
            <label>
              <span>Creative User</span>
              <select v-model="assignmentForm.creativeUsername">
                <option value="">Select creative user</option>
                <option v-for="creative in creativeUsers" :key="creative.username" :value="creative.username">{{ creative.username }}</option>
              </select>
            </label>
            <label>
              <span>Assignment Note</span>
              <textarea v-model.trim="assignmentForm.assignmentNote" placeholder="Task note for creative"></textarea>
            </label>
          </div>

          <div class="assign-actions">
            <button class="btn" @click="assignFromSelectedOperation">Confirm Assign</button>
            <button class="btn secondary" @click="clearSelectedOperation">Cancel</button>
          </div>
        </div>
      </div>

      <div v-if="showDetailsModal" class="details-modal" @click.self="closeDetails">
        <div class="details-shell">
          <div class="details-topbar">
            <div>
              <p class="eyebrow">Campaign Overview</p>
              <h2>{{ selectedCampaignDetails.campaignName || (selectedDetailRow && selectedDetailRow.campaignName) || 'Campaign Details' }}</h2>
              <p class="details-sub">Visual campaign briefing with planning data, creative notes, and asset previews.</p>
            </div>
            <button class="icon-btn" @click="closeDetails">Close</button>
          </div>

          <div v-if="detailsLoading" class="empty-state">Loading campaign details...</div>
          <template v-else-if="selectedCampaignDetails && selectedDetailRow">
            <div class="hero-grid">
              <article class="hero-card status-card">
                <span class="hero-label">Workflow Status</span>
                <strong>{{ statusLabel(selectedDetailRow.status) }}</strong>
                <span class="status-pill" :class="statusClass(selectedDetailRow.status)">{{ statusLabel(selectedDetailRow.status) }}</span>
              </article>
              <article class="hero-card">
                <span class="hero-label">Assigned Creative</span>
                <strong>{{ selectedDetailRow.assignedCreative || '-' }}</strong>
                <small>Current assignee for production</small>
              </article>
              <article class="hero-card">
                <span class="hero-label">Campaign Window</span>
                <strong>{{ humanDate(selectedCampaignDetails.startDate) }} to {{ humanDate(selectedCampaignDetails.endDate) }}</strong>
                <small>Scheduled launch timeline</small>
              </article>
              <article class="hero-card accent">
                <span class="hero-label">Target Content</span>
                <strong>{{ selectedCampaignDetails.numberOfContent || '-' }}</strong>
                <small>Daily target: {{ selectedCampaignDetails.dailyContentCount || '-' }}</small>
              </article>
            </div>

            <div class="details-grid-visual">
              <article class="details-card">
                <h3>Campaign Brief</h3>
                <div class="facts-grid">
                  <div class="fact-card">
                    <span>Sponsor</span>
                    <strong>{{ selectedCampaignDetails.sponsorName || selectedDetailRow.sponsorName || '-' }}</strong>
                  </div>
                  <div class="fact-card">
                    <span>Platform</span>
                    <strong>{{ formatPlatform(selectedCampaignDetails.videoPlatformDistribution) }}</strong>
                  </div>
                  <div class="fact-card wide">
                    <span>Categories</span>
                    <strong>{{ formatCategories(selectedCampaignDetails.contentCategory) }}</strong>
                  </div>
                  <div class="fact-card">
                    <span>Distribution Ratio</span>
                    <strong>{{ formatDistribution(selectedCampaignDetails) }}</strong>
                  </div>
                  <div class="fact-card wide">
                    <span>Events</span>
                    <strong>{{ formatEventTags(selectedCampaignDetails.eventTags) }}</strong>
                  </div>
                </div>
              </article>

              <article class="details-card">
                <h3>Creative Direction</h3>
                <div class="copy-stack">
                  <div class="copy-card">
                    <span>Creative Note</span>
                    <p>{{ selectedCampaignDetails.creative || '-' }}</p>
                  </div>
                  <div class="copy-card">
                    <span>Instruction</span>
                    <p>{{ selectedCampaignDetails.instruction || '-' }}</p>
                  </div>
                  <div class="copy-card">
                    <span>Exact Schedule</span>
                    <p>{{ formatScheduleSummary(selectedCampaignDetails) }}</p>
                  </div>
                  <div class="copy-card">
                    <span>Remarks</span>
                    <p>{{ selectedCampaignDetails.remarks || '-' }}</p>
                  </div>
                </div>
              </article>
            </div>

            <article class="details-card branding-card">
              <h3>Branding Setup</h3>
              <div class="branding-grid">
                <div class="branding-pill" :class="{ active: selectedCampaignDetails.brandingThumbnail }">Thumbnail</div>
                <div class="branding-pill" :class="{ active: selectedCampaignDetails.brandingDog }">Dog</div>
                <div class="branding-pill" :class="{ active: selectedCampaignDetails.brandingStatic }">Static</div>
                <div class="branding-pill" :class="{ active: selectedCampaignDetails.brandingPhotocard }">Photocard</div>
                <div class="branding-pill" :class="{ active: selectedCampaignDetails.brandingReels }">Reels</div>
                <div class="branding-pill wide">Lshape: {{ selectedCampaignDetails.brandingLshapeVariations || '-' }}</div>
                <div class="branding-pill wide">OVC: {{ selectedCampaignDetails.brandingOvcVariations || '-' }}</div>
              </div>
            </article>

            <article class="details-card files-card">
              <div class="files-head">
                <h3>Creative Asset Preview</h3>
                <span class="files-sub">Requirement files shown as visual cards where possible</span>
              </div>
              <div v-if="hasRequirementFiles(selectedCampaignDetails.fileRequirements)" class="asset-groups">
                <section class="asset-group" v-for="(files, label) in selectedCampaignDetails.fileRequirements" :key="`detail-${label}`" v-if="Array.isArray(files) && files.length">
                  <div class="asset-group-head">
                    <h4>{{ prettyLabel(label) }}</h4>
                    <span>{{ files.length }} file{{ files.length > 1 ? 's' : '' }}</span>
                  </div>
                  <div class="asset-grid">
                    <a class="asset-card" v-for="file in files" :key="file.key || file.url" :href="file.url" target="_blank" rel="noopener noreferrer">
                      <div class="asset-preview">
                        <img v-if="isImageFile(file)" :src="file.url" :alt="file.name || 'Preview image'" class="preview-media" />
                        <video v-else-if="isVideoFile(file)" :src="file.url" class="preview-media" muted playsinline preload="metadata"></video>
                        <div v-else class="preview-fallback">{{ fileExtensionLabel(file) }}</div>
                      </div>
                      <div class="asset-meta">
                        <strong>{{ file.name || 'Uploaded file' }}</strong>
                        <span>{{ prettyLabel(label) }}</span>
                      </div>
                    </a>
                  </div>
                </section>
              </div>
              <div v-else class="empty-state">No creative files uploaded for this campaign yet.</div>
            </article>

            <article class="details-card deliverable-review-card" v-if="selectedAssignmentDetails">
              <div class="files-head">
                <h3>Creative Submission Files</h3>
                <span class="files-sub">DCM can review new files and keep older uploads accessible.</span>
              </div>

              <div class="deliverable-list" v-if="sortedSelectedDeliverables.length">
                <div class="deliverable-item" v-for="file in sortedSelectedDeliverables" :key="file.key || file.url">
                  <div>
                    <p class="deliverable-name">
                      {{ file.name || 'Deliverable' }}
                      <span v-if="file.isNew" class="new-pill">New</span>
                    </p>
                    <p class="deliverable-meta">
                      Uploaded by {{ file.uploadedBy || selectedAssignmentDetails.creativeUsername || '-' }} at {{ formatDate(file.uploadedAt) }}
                    </p>
                  </div>
                  <a class="btn ghost small" :href="file.url" target="_blank" rel="noopener noreferrer">Open</a>
                </div>
              </div>
              <div v-else class="empty-state">Creative has not submitted files yet.</div>

              <div class="review-actions" v-if="canShowReviewActions">
                <label class="field">
                  <span>Feedback Note (Optional)</span>
                  <textarea v-model.trim="reviewForm.feedbackNote" placeholder="Write feedback for creative team"></textarea>
                </label>
                <p v-if="approvalHint" class="approval-hint">{{ approvalHint }}</p>
                <div class="assign-actions">
                  <button class="btn" :disabled="reviewSaving" @click="approveCreativeFromDetails">{{ reviewSaving ? 'Processing...' : 'Approve Creative' }}</button>
                  <button class="btn secondary" :disabled="reviewSaving" @click="sendFeedbackFromDetails">Send Feedback</button>
                </div>
              </div>
            </article>
          </template>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import MenuBar from './MenuBar.vue';
import api from '../api';
import sponsorService from '../services/sponsorService';
import {
  campaignStatusLabel,
  canAcceptCampaign,
  canAssignCreative,
  canPublishCampaign,
  isCampaignCompleted,
} from '../utils/campaignLifecycle';

export default {
  name: 'DcmManagement',
  components: {
    MenuBar,
  },
  data() {
    return {
      role: '',
      isSuperUser: false,
      operations: [],
      creativeUsers: [],
      selectedOperation: null,
      selectedDetailRow: null,
      selectedCampaignDetails: null,
      detailsLoading: false,
      showDetailsModal: false,
      assignmentForm: {
        creativeUsername: '',
        assignmentNote: '',
      },
      reviewForm: {
        feedbackNote: '',
      },
      reviewSaving: false,
      filters: {
        fromDate: '',
        toDate: '',
        status: '',
        assignedPerson: '',
      },
      message: '',
    };
  },
  computed: {
    isDcmOrSuper() {
      return this.isSuperUser || String(this.role).toLowerCase() === 'dcm';
    },
    filteredOperations() {
      return this.operations.filter(row => {
        if (this.filters.status && row.status !== this.filters.status) return false;
        if (this.filters.assignedPerson && row.assignedCreative !== this.filters.assignedPerson) return false;

        const createdDate = row.createdAt ? new Date(row.createdAt) : null;
        if (this.filters.fromDate && createdDate && createdDate < new Date(`${this.filters.fromDate}T00:00:00`)) return false;
        if (this.filters.toDate && createdDate && createdDate > new Date(`${this.filters.toDate}T23:59:59`)) return false;
        return true;
      });
    },
    selectedAssignmentDetails() {
      const assignment = this.selectedCampaignDetails && this.selectedCampaignDetails.assignment;
      return assignment && typeof assignment === 'object' ? assignment : null;
    },
    sortedSelectedDeliverables() {
      const assignment = this.selectedAssignmentDetails;
      const deliverables = assignment && Array.isArray(assignment.deliverables) ? assignment.deliverables : [];
      return [...deliverables].sort((a, b) => {
        const aTime = new Date(a && a.uploadedAt || 0).getTime();
        const bTime = new Date(b && b.uploadedAt || 0).getTime();
        return bTime - aTime;
      });
    },
    canShowReviewActions() {
      const assignment = this.selectedAssignmentDetails;
      if (!assignment || !assignment.id) return false;
      return ['creative_production', 'creative_approved'].includes(String(assignment.status || ''));
    },
    canApproveDetails() {
      const assignment = this.selectedAssignmentDetails;
      if (!assignment || String(assignment.status || '') !== 'creative_production') return false;
      return this.sortedSelectedDeliverables.length > 0;
    },
    approvalHint() {
      const assignment = this.selectedAssignmentDetails;
      if (!assignment) return '';
      const status = String(assignment.status || '');
      if (status !== 'creative_production') {
        return 'Approval is available only when assignment is in Creative Production status.';
      }
      if (!this.sortedSelectedDeliverables.length) {
        return 'Creative must upload at least one deliverable before approval.';
      }
      return '';
    },
  },
  async mounted() {
    await this.loadRoleContext();
    await this.loadOperationData();
  },
  methods: {
    async loadRoleContext() {
      try {
        const response = await api.get('/check');
        this.role = response.data.role || '';
        this.isSuperUser = !!response.data.isSuperUser;
      } catch (error) {
        this.role = '';
        this.isSuperUser = false;
      }
    },
    async loadOperationData() {
      this.message = '';
      try {
        if (this.isDcmOrSuper) {
          const [operationsResponse, creativesResponse] = await Promise.all([
            sponsorService.getDcmOperations(),
            sponsorService.getCreativeUsers(),
          ]);

          this.operations = operationsResponse.items || [];
          this.creativeUsers = creativesResponse.items || [];

          const campaignId = this.$route.query && this.$route.query.campaignId;
          if (campaignId) {
            const preselected = this.operations.find(item => item.campaignId === campaignId);
            if (preselected && this.canAssignCreativeStatus(preselected.status)) {
              this.openAssignPanel(preselected);
            }
          }
        }
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load operation data';
      }
    },
    async openDetails(row) {
      if (!row || !row.campaignId) {
        this.message = 'Campaign id missing for this row';
        return;
      }

      this.showDetailsModal = true;
      this.detailsLoading = true;
      this.selectedDetailRow = row;
      this.selectedCampaignDetails = null;
      this.reviewForm.feedbackNote = '';

      try {
        const response = await sponsorService.getDcmCampaignDetails(row.campaignId);
        this.selectedCampaignDetails = response.item || null;
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load campaign details';
      } finally {
        this.detailsLoading = false;
      }
    },
    closeDetails() {
      this.showDetailsModal = false;
      this.detailsLoading = false;
      this.selectedDetailRow = null;
      this.selectedCampaignDetails = null;
      this.reviewForm.feedbackNote = '';
      this.reviewSaving = false;
    },
    async approveCreativeFromDetails() {
      const assignment = this.selectedAssignmentDetails;
      if (!assignment || !assignment.id) {
        this.message = 'Assignment details are missing';
        return;
      }
      if (!this.canApproveDetails) {
        this.message = 'Creative must submit files before approval';
        return;
      }

      this.reviewSaving = true;
      try {
        await sponsorService.updateAssignmentStatus(assignment.id, 'creative_approved');
        this.message = 'Creative approved and campaign is ready for publishing';
        await this.refreshDetailsAfterReview();
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to approve creative';
      } finally {
        this.reviewSaving = false;
      }
    },
    async sendFeedbackFromDetails() {
      const assignment = this.selectedAssignmentDetails;
      if (!assignment || !assignment.id) {
        this.message = 'Assignment details are missing';
        return;
      }

      this.reviewSaving = true;
      try {
        await sponsorService.updateAssignmentStatus(assignment.id, 'creative_production', {
          feedbackNote: this.reviewForm.feedbackNote || '',
        });
        this.message = this.reviewForm.feedbackNote
          ? 'Feedback sent to creative team'
          : 'Moved back to creative production';
        this.reviewForm.feedbackNote = '';
        await this.refreshDetailsAfterReview();
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to send feedback';
      } finally {
        this.reviewSaving = false;
      }
    },
    async refreshDetailsAfterReview() {
      const campaignId = this.selectedDetailRow && this.selectedDetailRow.campaignId;
      await this.loadOperationData();
      if (!campaignId) return;

      const latestRow = this.operations.find(item => item.campaignId === campaignId);
      if (latestRow) {
        this.selectedDetailRow = latestRow;
      }

      try {
        const response = await sponsorService.getDcmCampaignDetails(campaignId);
        this.selectedCampaignDetails = response.item || null;
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to refresh campaign details';
      }
    },
    openAssignPanel(row) {
      if (!this.canAssignCreativeStatus(row.status)) {
        this.message = 'Task must be accepted before assigning creative';
        return;
      }
      this.selectedOperation = row;
      this.assignmentForm = {
        creativeUsername: '',
        assignmentNote: '',
      };
    },
    async acceptOperation(row) {
      try {
        await sponsorService.acceptDcmCampaignTask(row.campaignId);
        this.message = `Status updated to ${this.statusLabel('task_accepted')}`;
        await this.loadOperationData();
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to accept task';
      }
    },
    clearSelectedOperation() {
      this.selectedOperation = null;
      this.assignmentForm = {
        creativeUsername: '',
        assignmentNote: '',
      };
    },
    async assignFromSelectedOperation() {
      if (!this.selectedOperation) return;
      const creativeUsername = (this.assignmentForm.creativeUsername || '').trim();
      if (!creativeUsername) {
        this.message = 'Please select a creative user before assignment';
        return;
      }

      try {
        await sponsorService.assignCampaignToCreative({
          campaignId: this.selectedOperation.campaignId,
          creativeUsername,
          assignmentNote: this.assignmentForm.assignmentNote || '',
        });
        this.message = `Campaign assigned to ${creativeUsername}`;
        this.clearSelectedOperation();
        await this.loadOperationData();
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to assign campaign';
      }
    },
    openPublishing(row) {
      this.$router.push({ path: '/upload-studio', query: { campaignId: row.campaignId } });
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString();
    },
    humanDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    canAcceptCampaignStatus(status) {
      return canAcceptCampaign(status);
    },
    canAssignCreativeStatus(status) {
      return canAssignCreative(status);
    },
    canPublishCampaignStatus(status) {
      return canPublishCampaign(status);
    },
    isCompletedCampaignStatus(status) {
      return isCampaignCompleted(status);
    },
    statusLabel(status) {
      return campaignStatusLabel(status);
    },
    statusClass(status) {
      return `status-${String(status || '').toLowerCase()}`;
    },
    statusRowClass(status) {
      return `row-${String(status || '').toLowerCase()}`;
    },
    formatCategories(value) {
      if (!value) return '-';
      if (Array.isArray(value)) {
        return value.length ? value.join(', ') : '-';
      }
      return String(value);
    },
    formatPlatform(value) {
      if (!value) return '-';
      const platform = String(value).toLowerCase();
      if (platform === 'fb') return 'FB';
      if (platform === 'yt') return 'YT';
      if (platform === 'both') return 'Both';
      return value;
    },
    formatDistribution(campaign) {
      const distribution = campaign && campaign.contentDistribution;
      if (distribution && Array.isArray(distribution.items) && distribution.items.length) {
        return distribution.items
          .map(item => `${item.category}: ${item.percent}%`)
          .join(', ');
      }
      return (campaign && campaign.distributionRatio) || '-';
    },
    formatEventTags(tags) {
      if (!Array.isArray(tags) || !tags.length) return '-';
      return tags.join(', ');
    },
    formatScheduleSummary(campaign) {
      const rows = campaign && Array.isArray(campaign.scheduleEntries) ? campaign.scheduleEntries : [];
      if (!rows.length) return (campaign && campaign.exactSchedule) || '-';
      return rows
        .map(item => {
          const parts = [];
          if (item.date) parts.push(item.date);
          if (item.category) parts.push(item.category);
          if (item.plannedCount) parts.push(`count ${item.plannedCount}`);
          if (item.instruction) parts.push(item.instruction);
          return parts.join(' | ');
        })
        .filter(Boolean)
        .join(', ');
    },
    prettyLabel(label) {
      return String(label || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    fileExtensionLabel(file) {
      const extension = (file && file.extension) || ((file && file.name && file.name.split('.').pop()) || 'FILE');
      return String(extension).toUpperCase();
    },
    isImageFile(file) {
      const extension = String((file && file.extension) || '').toLowerCase();
      return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension);
    },
    isVideoFile(file) {
      const extension = String((file && file.extension) || '').toLowerCase();
      return ['mp4', 'mov', 'webm'].includes(extension);
    },
    hasRequirementFiles(fileRequirements) {
      if (!fileRequirements || typeof fileRequirements !== 'object') return false;
      return Object.values(fileRequirements).some(files => Array.isArray(files) && files.length);
    },
  },
};
</script>

<style scoped>
.dcm-page {
  display: flex;
  min-height: 100vh;
  background: #f4f7fb;
}

.content {
  flex: 1;
  margin-left: 250px;
  padding: 24px;
}

.page-header h1 {
  margin: 0;
  color: #1f2d3d;
}

.page-header p {
  margin: 8px 0 16px;
  color: #5f7389;
}

.panel {
  border: 1px solid #dfe7f2;
  border-radius: 12px;
  background: #fff;
  padding: 14px;
  margin-bottom: 14px;
}

.panel h2 {
  margin: 0;
  color: #233244;
}

.sub {
  margin: 6px 0 12px;
  color: #61758b;
  font-size: 13px;
}

.filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}

.filters label {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filters label span {
  font-size: 12px;
  font-weight: 700;
  color: #52677f;
}

.filters input,
.filters select {
  border: 1px solid #d3dde9;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.ops-table-wrap {
  margin-top: 8px;
}

.ops-table {
  width: 100%;
  border-collapse: collapse;
}

.ops-table thead {
  background: #f2f6fc;
}

.ops-table th,
.ops-table td {
  border-bottom: 1px solid #e9eef5;
  text-align: left;
  padding: 10px;
  font-size: 13px;
}

.action-cell {
  white-space: nowrap;
}

.ops-table tbody tr:hover {
  background: #f8fbff;
}

.mini {
  margin: 3px 0 0;
  color: #71869b;
  font-size: 12px;
}

.assign-panel {
  margin-top: 12px;
  border: 1px solid #d6e1f0;
  border-radius: 10px;
  padding: 12px;
  background: #f7fbff;
}

.assign-panel h3 {
  margin: 0;
  color: #274368;
}

.assign-panel p {
  margin: 6px 0 10px;
  color: #5e748c;
}

.assign-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.assign-grid label {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.assign-grid label span {
  font-size: 12px;
  font-weight: 700;
  color: #51667e;
}

.assign-grid select,
.assign-grid textarea {
  border: 1px solid #d3dde9;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.assign-grid textarea {
  min-height: 78px;
  resize: vertical;
}

.assign-actions {
  margin-top: 10px;
  display: flex;
  gap: 8px;
}

.deliverable-list {
  display: grid;
  gap: 8px;
}

.deliverable-item {
  border: 1px solid #dce8f4;
  border-radius: 10px;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.deliverable-name {
  margin: 0;
  font-weight: 700;
  color: #294862;
  display: flex;
  align-items: center;
  gap: 6px;
}

.deliverable-meta {
  margin: 3px 0 0;
  color: #657b91;
  font-size: 12px;
}

.new-pill {
  border-radius: 999px;
  padding: 2px 7px;
  font-size: 10px;
  font-weight: 700;
  background: #dcfce7;
  color: #166534;
}

.review-actions {
  margin-top: 10px;
  border-top: 1px solid #e6edf7;
  padding-top: 10px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.field label {
  font-size: 12px;
  font-weight: 700;
  color: #4f647b;
}

.field select,
.field textarea {
  border: 1px solid #d3dde9;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.field textarea {
  min-height: 70px;
  resize: vertical;
}

.empty-mini {
  margin: 0;
  font-size: 12px;
  color: #71859a;
}

.message-banner {
  margin: 8px 0 12px;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid #d5e0ec;
  background: #f5f9ff;
  color: #2f4a60;
  font-size: 13px;
  font-weight: 600;
}

.approval-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #5a7088;
}

.btn {
  border: none;
  margin-top: 10px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: #fff;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
}

.btn.ghost {
  background: #eff6ff;
  color: #1d4ed8;
}

.btn.secondary {
  background: #6c757d;
}

.status-pill.status-campaign_initiated {
  background: #eef4ff;
  color: #2c5cc5;
}

.status-pill.status-task_accepted {
  background: #e8f7ef;
  color: #18794e;
}

.status-pill.status-creative_production {
  background: #fff4e5;
  color: #9a5b00;
}

.status-pill.status-creative_approved {
  background: #f3f0ff;
  color: #6941c6;
}

.status-pill.status-publishing {
  background: #e0f2fe;
  color: #075985;
}

.status-pill.status-completed {
  background: #dcfce7;
  color: #166534;
}

.empty-state {
  border: 1px dashed #c7d5e6;
  border-radius: 10px;
  background: #f8fbff;
  padding: 14px;
  color: #5a6f85;
  text-align: center;
  font-weight: 500;
}

.details-modal {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(15, 23, 42, 0.52);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.details-shell {
  width: min(1180px, 96vw);
  max-height: 92vh;
  overflow-y: auto;
  border-radius: 24px;
  background: linear-gradient(180deg, #fcfeff 0%, #f5f9ff 100%);
  border: 1px solid #d7e4f2;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
  padding: 22px;
}

.details-topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.eyebrow {
  margin: 0 0 4px;
  color: #5f7389;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 11px;
  font-weight: 700;
}

.details-topbar h2 {
  margin: 0;
  color: #1f2d3d;
  font-size: 28px;
}

.details-sub {
  margin: 6px 0 0;
  color: #64788f;
}

.icon-btn {
  border: 1px solid #cddbec;
  border-radius: 999px;
  background: #ffffff;
  color: #274368;
  padding: 9px 14px;
  font-weight: 700;
  cursor: pointer;
}

.hero-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.hero-card {
  border: 1px solid #dce8f4;
  border-radius: 18px;
  background: #ffffff;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(37, 99, 235, 0.08);
}

.hero-card.accent {
  background: linear-gradient(135deg, #fef7e7 0%, #ffffff 100%);
}

.status-card {
  background: linear-gradient(135deg, #eef6ff 0%, #ffffff 100%);
}

.hero-label {
  display: block;
  color: #61758b;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.hero-card strong {
  display: block;
  margin: 8px 0 10px;
  color: #1f3654;
  font-size: 20px;
  line-height: 1.3;
}

.hero-card small {
  color: #6b8097;
  font-size: 12px;
}

.details-grid-visual {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}

.details-card {
  border: 1px solid #dce8f4;
  border-radius: 20px;
  background: #ffffff;
  padding: 16px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
}

.details-card h3 {
  margin: 0 0 12px;
  color: #233244;
  font-size: 16px;
}

.facts-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.fact-card {
  border: 1px solid #ebf1f7;
  border-radius: 14px;
  background: #f8fbff;
  padding: 12px;
}

.fact-card.wide {
  grid-column: span 2;
}

.fact-card span,
.copy-card span {
  display: block;
  color: #6b7f94;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.fact-card strong {
  display: block;
  margin-top: 6px;
  color: #22384f;
  font-size: 14px;
}

.copy-stack {
  display: grid;
  gap: 10px;
}

.copy-card {
  border: 1px solid #ebf1f7;
  border-radius: 14px;
  background: #fbfdff;
  padding: 12px;
}

.copy-card p {
  margin: 6px 0 0;
  color: #50657d;
  line-height: 1.6;
  font-size: 13px;
}

.branding-card {
  margin-bottom: 14px;
}

.branding-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.branding-pill {
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid #dbe7f3;
  background: #f8fbff;
  color: #60758d;
  font-weight: 700;
  font-size: 12px;
}

.branding-pill.active {
  border-color: #8bb8f3;
  background: #eaf4ff;
  color: #1958b7;
}

.branding-pill.wide {
  border-radius: 14px;
}

.files-card {
  background: linear-gradient(180deg, #ffffff 0%, #f9fcff 100%);
}

.files-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.files-sub {
  color: #6b8097;
  font-size: 12px;
}

.asset-groups {
  display: grid;
  gap: 14px;
}

.asset-group {
  border: 1px solid #e7eef7;
  border-radius: 18px;
  background: #fcfdff;
  padding: 14px;
}

.asset-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.asset-group-head h4 {
  margin: 0;
  color: #274368;
}

.asset-group-head span {
  color: #6a7e94;
  font-size: 12px;
}

.asset-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
}

.asset-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid #dfe8f2;
  border-radius: 16px;
  background: #ffffff;
  padding: 10px;
  text-decoration: none;
}

.asset-preview {
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  border: 1px solid #dbe7f2;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fbff 100%);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.preview-fallback {
  font-size: 16px;
  font-weight: 800;
  color: #476486;
  letter-spacing: 0.06em;
}

.asset-meta strong {
  display: block;
  color: #22384f;
  font-size: 13px;
  line-height: 1.4;
  word-break: break-word;
}

.asset-meta span {
  display: block;
  margin-top: 4px;
  color: #6b8097;
  font-size: 12px;
}

@media (max-width: 1200px) {
  .filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .hero-grid,
  .details-grid-visual {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .content {
    margin-left: 200px;
    padding: 16px;
  }

  .details-shell {
    padding: 16px;
  }

  .details-topbar,
  .files-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-grid,
  .details-grid-visual,
  .facts-grid,
  .asset-grid {
    grid-template-columns: 1fr;
  }

  .filters,
  .assign-grid {
    grid-template-columns: 1fr;
  }

  .fact-card.wide {
    grid-column: span 1;
  }
}
</style>
